import { useMemo, useState } from "react";
import { Sparkles, Activity, PieChart, Target, Award } from "lucide-react";
import { CollapsibleCard } from "./CollapsibleCard";
import { Kpi, Panel, WeeklyBars, ShareBars, Conversions, Extensions, ThemesAndAssets } from "./pmax/PmaxParts";
import { BenchmarkBadge, DailyChart } from "./pmax/PmaxDaily";
import { fmtNum, fmtPeriod, computeDelta, pmaxImpressions, pmaxClicks } from "./pmax/pmaxUtils";

const Header = ({ rec, count }) => (
  <div className="flex items-center gap-4">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-lime-300 to-cyan-400 flex items-center justify-center flex-shrink-0">
      <Sparkles className="w-7 h-7 text-[#0a3d54]" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-lime-300 text-xs font-semibold uppercase tracking-wider">Google Performance Max</p>
      <p className="text-white text-lg md:text-xl font-bold">Google AI, working every screen</p>
      <p className="text-slate-400 text-xs mt-0.5">{fmtPeriod(rec.report_start, rec.report_end)}{count > 1 ? ` · ${count} reports` : ""}</p>
    </div>
    <span className="hidden sm:inline px-3 py-1 bg-lime-400/15 text-lime-300 text-xs font-semibold rounded-full border border-lime-400/30" data-testid="pmax-header-events">
      {fmtNum(rec.events)} actions
    </span>
  </div>
);

const PeriodSwitcher = ({ records, idx, setIdx }) => (
  <div className="flex flex-wrap gap-2 mb-5" data-testid="pmax-period-switcher">
    {records.map((r, i) => (
      <button
        key={r.id || i}
        onClick={() => setIdx(i)}
        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${i === idx ? "bg-lime-400 text-[#0a3d54] border-lime-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}
        data-testid={`pmax-period-${i}`}
      >
        {fmtPeriod(r.report_start, r.report_end)}
      </button>
    ))}
  </div>
);

const Narrative = ({ rec, clientName }) => (
  <div className="bg-gradient-to-r from-lime-400/10 to-cyan-400/10 border border-lime-300/20 rounded-2xl p-5 mb-5" data-testid="pmax-narrative">
    <p className="text-slate-200 text-sm md:text-base leading-relaxed">
      Google&apos;s AI placed {clientName}&apos;s ads <span className="text-white font-semibold">{fmtNum(pmaxImpressions(rec))} times</span> across
      YouTube, Search, Display, Discover, Gmail and Google Maps. People took <span className="text-lime-300 font-semibold">{fmtNum(rec.events)} actions</span>: clicks,
      video views, engagements and conversions. That&apos;s a <span className="text-white font-semibold">{Number(rec.event_rate || 0).toFixed(2)}% action rate</span>, with the
      AI learning every week which screens and messages work best.
    </p>
  </div>
);

export default function PmaxStoryCard({ records = [], clientName = "your brand", story = null }) {
  const sorted = useMemo(() => [...records].sort((a, b) => (b.report_end || "").localeCompare(a.report_end || "")), [records]);
  const [idx, setIdx] = useState(0);
  if (sorted.length === 0) return null;
  const rec = sorted[Math.min(idx, sorted.length - 1)];
  const prior = sorted[Math.min(idx, sorted.length - 1) + 1];
  const hasConv = (rec.click_conversions_total || 0) + (rec.view_through_total || 0) > 0;
  const summary = rec.summary;

  return (
    <CollapsibleCard
      testId="pmax-section"
      containerClass="bg-gradient-to-br from-lime-500/10 via-cyan-500/10 to-teal-500/10 rounded-2xl p-6 md:p-8 border border-lime-300/20"
      chevronClass="text-lime-300"
      header={<Header rec={rec} count={sorted.length} />}
      story={story}
    >
      {sorted.length > 1 && <PeriodSwitcher records={sorted} idx={idx} setIdx={setIdx} />}
      <Narrative rec={rec} clientName={clientName} />
      {summary?.ctr > 0 && <BenchmarkBadge ctr={summary.ctr} />}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${summary ? "lg:grid-cols-5" : "lg:grid-cols-3"} gap-3 mb-5`}>
        <Kpi label="Times your ads were seen" value={fmtNum(pmaxImpressions(rec))} delta={prior && computeDelta(pmaxImpressions(rec), pmaxImpressions(prior))} testId="pmax-kpi-impressions" />
        {summary && (
          <Kpi label="Clicks" value={fmtNum(pmaxClicks(rec))} delta={prior?.summary && computeDelta(pmaxClicks(rec), pmaxClicks(prior))} testId="pmax-kpi-clicks" />
        )}
        {summary && (
          <Kpi label="Click rate" value={`${Number(summary.ctr || 0).toFixed(2)}%`} sub="Clicks per ad seen" delta={prior?.summary && computeDelta(summary.ctr, prior.summary.ctr)} testId="pmax-kpi-ctr" />
        )}
        <Kpi label="Actions taken" value={fmtNum(rec.events)} sub="Clicks, video views, engagements, conversions" delta={prior && computeDelta(rec.events, prior.events)} testId="pmax-kpi-events" />
        <Kpi label="Action rate" value={`${Number(rec.event_rate || 0).toFixed(2)}%`} sub="Actions per ad seen" delta={prior && computeDelta(rec.event_rate, prior.event_rate)} testId="pmax-kpi-rate" />
      </div>

      {summary?.daily?.length > 0 && (
        <div className="mb-5">
          <Panel title="Day by day" icon={Activity} testId="pmax-daily"><DailyChart daily={summary.daily} /></Panel>
        </div>
      )}

      {(rec.weekly?.length > 0 || rec.event_types?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-5">
          {rec.weekly?.length > 0 && (
            <div className="lg:col-span-3"><Panel title="Actions, week by week" icon={Activity} testId="pmax-weekly"><WeeklyBars weekly={rec.weekly} /></Panel></div>
          )}
          {rec.event_types?.length > 0 && (
            <div className="lg:col-span-2"><Panel title="What kind of actions" icon={PieChart} testId="pmax-event-types"><ShareBars rows={rec.event_types} testId="pmax-event-type-bars" /></Panel></div>
          )}
        </div>
      )}

      {hasConv && (
        <div className="mb-5">
          <Panel title="Website visits that counted" icon={Target} testId="pmax-conversions"><Conversions rec={rec} /></Panel>
        </div>
      )}

      {rec.extensions?.length > 0 && (
        <div className="mb-5">
          <Panel title="Your best performing ad add-ons" icon={Award} testId="pmax-extensions">
            <Extensions extensions={rec.extensions} types={rec.extension_types || []} />
          </Panel>
        </div>
      )}

      {(rec.search_themes?.length > 0 || rec.creative_assets_total > 0) && <ThemesAndAssets rec={rec} />}
    </CollapsibleCard>
  );
}
